import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { brandJsonSchema } from '@fametrue/shared';
import template from './templates/brand-esempio-parametrico.json';

/**
 * "Scarica ▾" nella testata del caricamento dati: due file per chi prepara
 * i JSON a monte.
 *
 * - **Schema JSON**: generato qui, al momento del click, dallo stesso
 *   `brandSchema` che valida l'import (`brandJsonSchema()` in shared). Non
 *   è un file statico che può restare indietro rispetto al validatore.
 * - **JSON precompilato**: un oggetto brand completo che passa la
 *   validazione, nella forma parametrica (tabelle del blocco 01 come
 *   `blocks`, non come HTML). È una copia di
 *   `samples/brand-esempio-parametrico.json`: un test la tiene identica,
 *   perché `samples/` non entra nell'immagine Docker e da qui non si può
 *   importare.
 */
export function DownloadMenu(): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function download(filename: string, data: unknown): void {
    const blob = new Blob([JSON.stringify(data, null, 2) + '\n'], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
  }

  return (
    <div className="admin-menu" ref={container}>
      <button
        type="button"
        className="admin-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('admin.import.download')}
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
          <path d="M3 4.5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className="admin-menu-panel" role="menu">
          <button
            type="button"
            role="menuitem"
            className="admin-menu-item"
            onClick={() => download('fametrue-brand.schema.json', brandJsonSchema())}
          >
            <span className="admin-menu-item-title">{t('admin.import.downloadSchema')}</span>
            <span className="admin-menu-item-hint">{t('admin.import.downloadSchemaHint')}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="admin-menu-item"
            onClick={() => download('fametrue-brand-esempio.json', template)}
          >
            <span className="admin-menu-item-title">{t('admin.import.downloadTemplate')}</span>
            <span className="admin-menu-item-hint">{t('admin.import.downloadTemplateHint')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
